import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { CacheRequestConfig, setupCache } from 'axios-cache-interceptor';
import { envConfig } from '../config';
import { AXIOS_CACHE_CONFIG } from './cache-config';
import {
  addApiRequestInterceptor,
  addApiResponseInterceptor,
  addGatewayRequestInterceptor,
  addGatewayResponseInterceptor,
} from './http-client-interceptors';

const removeTrailingSlash = (url?: string) => url?.replace(/\/+$/, '') ?? '';

const legacyBaseUrl = removeTrailingSlash(
  envConfig.REACT_APP_API_URL?.replace(/\/v1\/?$/, '')
);

const gatewayBaseUrl = removeTrailingSlash(envConfig.REACT_APP_GATEWAY_API_URL);

type AddInterceptor = (httpClient: AxiosInstance) => void;

const createHttpClient = (
  baseURL: string,
  addRequestInterceptor: AddInterceptor,
  addResponseInterceptor: AddInterceptor
) => {
  const httpClient = setupCache(axios.create({ baseURL }), AXIOS_CACHE_CONFIG);

  addRequestInterceptor(httpClient);
  addResponseInterceptor(httpClient);

  return httpClient;
};

export const apiClient = createHttpClient(
  legacyBaseUrl,
  addApiRequestInterceptor,
  addApiResponseInterceptor
);

export const gatewayClient = createHttpClient(
  gatewayBaseUrl,
  addGatewayRequestInterceptor,
  addGatewayResponseInterceptor
);

const responseBody = <T>(response: AxiosResponse<T> | undefined) => {
  if (!response) throw new Error('Undefined response');
  return response.data;
};

const createRequests = (httpClient: AxiosInstance) => ({
  get: <T>(url: string, config?: CacheRequestConfig<any>) =>
    httpClient.get<T>(url, config).then(responseBody),
  post: <T>(url: string, body?: {}, config?: CacheRequestConfig<T>) =>
    httpClient.post<T>(url, body, config).then(responseBody),
  put: <T>(url: string, body: {}) =>
    httpClient.put<T>(url, body).then(responseBody),
  del: <T>(url: string) => httpClient.delete<T>(url).then(responseBody),
});

export const apiRequests = createRequests(apiClient);

export const gatewayRequests = createRequests(gatewayClient);
